// S24: Audit Log API Endpoints
import { apiClient } from './client';

export interface AuditLog {
  id: string;
  at: string;
  event: string;
  actor_type: 'user' | 'system';
  actor_id?: string;
  actor_name?: string;
  entity_type: string;
  entity_id?: string;
  metadata_preview: Array<{ key: string; value: string }>;
  ip_address?: string;
}

export interface AuditLogDetail {
  id: string;
  at: string;
  event: string;
  actor_type: 'user' | 'system';
  actor_id?: string;
  actor: {
    type: 'user' | 'system';
    id?: string;
    full_name?: string;
    email?: string;
    role?: string;
    deleted?: boolean;
  };
  entity_type: string;
  entity_id?: string;
  entity?: {
    id: string;
    deleted?: boolean;
    [key: string]: any;
  };
  metadata: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  updated_at: string;
}

export interface AuditLogFilters {
  event?: string;
  entity_type?: string;
  entity_id?: string;
  actor_id?: string;
  actor_type?: 'user' | 'system';
  from?: string;
  to?: string;
  page?: number;
  per?: number;
}

export interface AuditLogListResponse {
  audit_logs: AuditLog[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  filters: AuditLogFilters;
}

export interface AuditLogDetailResponse {
  audit_log: AuditLogDetail;
}

export interface EventTypesResponse {
  events: string[];
}

export interface EntityTypesResponse {
  entity_types: string[];
}

export const auditLogEndpoints = {
  // Get audit logs with filters
  async getAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLogListResponse> {
    const params = new URLSearchParams();
    
    if (filters.event) params.append('event', filters.event);
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.entity_id) params.append('entity_id', filters.entity_id);
    if (filters.actor_id) params.append('actor_id', filters.actor_id);
    if (filters.actor_type) params.append('actor_type', filters.actor_type);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per) params.append('per', filters.per.toString());
    
    return await apiClient.get(`/api/admin/audit_logs?${params.toString()}`);
  },

  // Get single audit log detail
  async getAuditLog(id: string): Promise<AuditLogDetailResponse> {
    return await apiClient.get(`/api/admin/audit_logs/${id}`);
  },

  // Get list of all available event types
  async getEventTypes(): Promise<EventTypesResponse> {
    return await apiClient.get('/api/admin/audit_logs/events');
  },

  // Get list of all entity types
  async getEntityTypes(): Promise<EntityTypesResponse> {
    return await apiClient.get('/api/admin/audit_logs/entity_types');
  },
};
